// src/pages/auth/LoginPage.tsx
// Login page with dynamic OAuth providers loaded from API.

import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useAuthn, useAuthnAuth, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { ListPublicOAuth2Providers } from '@core/api/endpoints/authn/list_public_oauth2_providers';
import type { PublicOAuth2Provider } from '@core/types/authn';
import { OtpInput } from '../../components/OtpInput';
import { validateSignIn } from '../../features/authn/logic';
import type { SignInFormState } from '../../features/authn/types';
import { AuthLayout } from './AuthLayout';

type LoginMode = 'password' | 'otp';

export const LoginPage = observer(() => {
  const auth = useAuth();
  const authnStore = useAuthn();
  const authnAuth = useAuthnAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string; email?: string } | null) ?? null;

  const [mode, setMode] = useState<LoginMode>('password');
  const [form, setForm] = useState<SignInFormState>({ email: flash?.email ?? '', password: '', rememberMe: false });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [socialLoading, setSocialLoading] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  // Dynamic OAuth providers
  const [oauthProviders, setOauthProviders] = useState<PublicOAuth2Provider[]>([]);

  useEffect(() => {
    ListPublicOAuth2Providers()
      .then((resp) => {
        const items = resp.providers?.items ?? [];
        setOauthProviders(items.sort((a, b) => a.priority - b.priority));
      })
      .catch(() => {});
  }, []);

  if (auth.isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleChange = (field: keyof SignInFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateSignIn(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    try {
      const result = await auth.login({ email: form.email, password: form.password });
      if (result.two_factor_required) {
        navigate('/2fa-challenge');
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setErrors({ form: err });
    }
  };

  const handleSendOtp = async () => {
    if (!form.email.trim()) {
      setErrors({ email: t('auth.emailRequired') });
      return;
    }
    setOtpLoading(true);
    setErrors({});
    try {
      await authnAuth.SendOneTimeCode({ email: form.email });
      setOtpSent(true);
    } catch (err) {
      setErrors({ form: err });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) {
      setErrors({ otp: t('auth.otpRequired') });
      return;
    }
    setOtpLoading(true);
    setErrors({});
    try {
      await authnAuth.SignInWithCode({ email: form.email, code: otpCode });
      navigate('/dashboard');
    } catch (err) {
      setErrors({ form: err });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSocialLogin = async (providerId: string) => {
    setSocialLoading(providerId);
    try {
      const resp = await authnStore.InitiateOAuth2Flow({
        entity: {} as never,
        provider_id: providerId,
        redirect_uri: `${window.location.origin}/oauth/callback`,
        state: crypto.randomUUID(),
        scopes: { items: [] },
      });
      window.location.href = resp.authorization_url;
    } catch {
      setSocialLoading('');
    }
  };

  const gridCols = oauthProviders.length === 1
    ? 'grid-cols-1'
    : oauthProviders.length === 2
      ? 'grid-cols-2'
      : 'grid-cols-2 sm:grid-cols-3';

  return (
    <AuthLayout>
      {/* Auth card */}
      <div className="w-full max-w-[480px] mx-auto bg-card backdrop-blur-xl border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Tab header */}
        <div className="flex border-b border-border">
          <button className="flex-1 py-4 text-sm font-bold border-b-2 border-primary text-foreground transition-colors">
            {t('auth.login')}
          </button>
          <Link
            to="/register"
            className="flex-1 py-4 text-sm font-bold text-center border-b-2 border-transparent text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('auth.register')}
          </Link>
        </div>

        <div className="p-6 sm:p-8">
          {/* Welcome section */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl font-bold text-foreground mb-2">{t('auth.loginSubtitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('auth.loginDescription')}
            </p>
          </div>

          {flash?.flash && !errors.form ? (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300">
              <svg className="mt-0.5 size-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{flash.flash}</span>
            </div>
          ) : null}

          {errors.form ? (
            <div className="mb-5">
              <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} />
            </div>
          ) : null}

          {/* Login mode toggle */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => { setMode('password'); setErrors({}); setOtpSent(false); setOtpCode(''); }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors ${mode === 'password' ? 'bg-primary/10 text-primary border border-primary/20' : 'text-muted-foreground hover:bg-accent'}`}
            >
              {t('auth.passwordLogin')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('otp'); setErrors({}); }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors ${mode === 'otp' ? 'bg-primary/10 text-primary border border-primary/20' : 'text-muted-foreground hover:bg-accent'}`}
            >
              {t('auth.otpLogin')}
            </button>
          </div>

          {mode === 'password' ? (
            /* Password login form */
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-email">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <input
                    id="login-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder="alex@company.com"
                    autoComplete="email"
                    className="w-full pl-10 pr-4 py-3 bg-muted border border-input rounded-xl focus:ring-2 focus:ring-ring/20 focus:border-ring outline-none transition-all text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                {errors.email ? <p className="mt-1 text-xs text-destructive">{errors.email as string}</p> : null}
              </div>

              {/* Password */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold text-foreground" htmlFor="login-password">
                    {t('auth.password')}
                  </label>
                  <Link to="/forgot-password" className="text-xs font-bold text-primary hover:underline">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange('password')}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-12 py-3 bg-muted border border-input rounded-xl focus:ring-2 focus:ring-ring/20 focus:border-ring outline-none transition-all text-foreground placeholder:text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    {showPassword ? (
                      <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                {errors.password ? <p className="mt-1 text-xs text-destructive">{errors.password as string}</p> : null}
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2">
                <input
                  id="remember"
                  type="checkbox"
                  checked={form.rememberMe}
                  onChange={handleChange('rememberMe')}
                  className="w-4 h-4 rounded border-input text-primary focus:ring-primary bg-muted"
                />
                <label htmlFor="remember" className="text-sm text-muted-foreground select-none">
                  {t('auth.rememberMe')}
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={auth.isLoading}
                className="w-full py-3.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
              >
                {auth.isLoading ? (
                  <svg className="animate-spin size-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                ) : (
                  <>
                    {t('auth.loginButton')}
                    <svg className="size-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </>
                )}
              </button>
            </form>
          ) : (
            /* OTP / Magic link login form */
            <form onSubmit={otpSent ? handleOtpSubmit : (e) => { e.preventDefault(); handleSendOtp(); }} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="otp-email">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <input
                    id="otp-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder="alex@company.com"
                    autoComplete="email"
                    disabled={otpSent}
                    className="w-full pl-10 pr-4 py-3 bg-muted border border-input rounded-xl focus:ring-2 focus:ring-ring/20 focus:border-ring outline-none transition-all text-foreground placeholder:text-muted-foreground disabled:opacity-60"
                  />
                </div>
                {errors.email ? <p className="mt-1 text-xs text-destructive">{errors.email as string}</p> : null}
              </div>

              {otpSent && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">
                      {t('auth.enterOtpCode')}
                    </label>
                    <p className="text-xs text-muted-foreground mb-3">{t('auth.otpSentTo', { email: form.email })}</p>
                    <OtpInput value={otpCode} onChange={setOtpCode} />
                    {errors.otp ? <p className="mt-1 text-xs text-destructive">{errors.otp as string}</p> : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setOtpSent(false); setOtpCode(''); }}
                    className="text-xs text-primary hover:underline"
                  >
                    {t('auth.changeEmail')}
                  </button>
                </>
              )}

              <button
                type="submit"
                disabled={otpLoading}
                className="w-full py-3.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
              >
                {otpLoading ? (
                  <svg className="animate-spin size-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                ) : otpSent ? (
                  t('auth.verifyCode')
                ) : (
                  t('auth.sendCode')
                )}
              </button>

              {otpSent && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpLoading}
                  className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('auth.resendCode')}
                </button>
              )}
            </form>
          )}

          {/* OAuth providers — only shown when providers exist */}
          {oauthProviders.length > 0 && (
            <>
              {/* Divider */}
              <div className="relative my-6 sm:my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-3 text-muted-foreground font-bold tracking-widest">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>

              {/* Dynamic social login buttons */}
              <div className={`grid ${gridCols} gap-3`}>
                {oauthProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={!!socialLoading}
                    onClick={() => handleSocialLogin(provider.id)}
                    className="flex items-center justify-center gap-2.5 py-3 px-4 border border-border rounded-xl hover:bg-accent transition-colors disabled:opacity-50 group"
                  >
                    {socialLoading === provider.id ? (
                      <svg className="animate-spin size-5 text-primary" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    ) : (
                      <>
                        {provider.icon_url ? (
                          <img src={provider.icon_url} alt="" className="h-5 w-5 object-contain shrink-0" />
                        ) : (
                          <svg className="h-5 w-5 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.813a4.5 4.5 0 00-6.364-6.364L4.5 8.25l4.5 4.5" />
                          </svg>
                        )}
                        <span className="text-sm font-medium text-foreground group-hover:text-accent-foreground">{provider.provider_name}</span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Card footer — security badge */}
        <div className="p-6 bg-muted border-t border-border text-center">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            {t('auth.securityBadge')}
          </p>
        </div>
      </div>
    </AuthLayout>
  );
});

export default LoginPage;
